import type { ReactNode } from 'react';
import { Suspense, lazy } from 'react';

import { Trans } from 'react-i18next';

import Typography from '@mui/material/Typography';

import LogoMemberIcon from '@/components/icons/logo-with-text-black.icon';
import Link from '@/components/molecules/Link/Link';
import ShieldIcon from '@/components/icons/shield.icon';
import Loading from '@/components/molecules/Loading';
import Skeleton from '@mui/material/Skeleton';
import { ErrorBoundary } from 'react-error-boundary';

const Animation = lazy(() => import('@/animations/Animation'));

function LoadingScreenWithAnimation({
  title,
  content,
  getAnimationData = async () => {
    const m = await import('@/animations/taking-too-long/taking-too-long.json');
    return m.default;
  },
  insteadOfAnimation,
}: {
  title: string | ReactNode;
  content: string | ReactNode;
  getAnimationData?: () => Promise<Record<string, unknown>>;
  insteadOfAnimation?: ReactNode;
}) {
  return (
    <Loading
      className="z-[100] h-full"
      slotProps={{ wrapper: { className: 'h-full' } }}
      showLoadingIcon={false}
      fullscreen
    >
      <div className="flex h-full flex-col items-center gap-5">
        <LogoMemberIcon />
        <div className="mt-20 flex flex-grow-1 flex-col items-center gap-2.5 md:mt-24 md:flex-grow-0">
          {!insteadOfAnimation ? (
            <ErrorBoundary fallback={<Skeleton height={200} width="80%" />}>
              <Suspense fallback={<Skeleton height={200} width="80%" />}>
                <Animation
                  getAnimationData={getAnimationData}
                  id="taking-too-long-animation"
                  width="80%"
                  height={200}
                  loop
                />
              </Suspense>
            </ErrorBoundary>
          ) : (
            insteadOfAnimation
          )}
          <Typography
            variant="h1"
            className="text-2xl font-normal text-[#5C5C5C]"
          >
            {title}
          </Typography>
          {content}
        </div>

        <Link
          href="/logout"
          className="mx-auto mb-12 flex w-1/2 items-center justify-center gap-2 text-center text-base font-bold text-primary/70 md:mt-8"
        >
          <ShieldIcon color="var(--color-primary)" />
          <Trans ns="common" i18nKey="logout-page.action" />
        </Link>
      </div>
    </Loading>
  );
}

export default LoadingScreenWithAnimation;
